import { useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppNotification } from '@feature/utils';

import { CitationForm } from './CitationForm';
import { useCitationEdit } from './model/CitationQuery';
import { CitationSchema, citationSchema, defaultCitationValues } from './model/CitationSchema';
import { Citation, PublishedIn } from './model/CitationTypes';

interface CitationEditProps {
  citation: Citation;
  publicationId: number;
}

function convert(citation: Citation): CitationSchema {
  return {
    ...citation,
    title: citation.title ?? '',
    publishedIn: citation.publishedIn ?? PublishedIn.JOURNAL,
    identifiers: {
      wosId: citation.identifiers?.wosId ?? '',
      doi: citation.identifiers?.doi ?? '',
      scopusId: citation.identifiers?.scopusId ?? '',
    },
    journal: citation.journal,
    publisher: citation.publisher,
    authors: citation.authors,
  };
}

export function CitationEdit({ citation, publicationId }: CitationEditProps) {
  const { t } = useTranslation();
  const { mutate, isPending, isError, isSuccess } = useCitationEdit(publicationId, citation.id);
  const notification = useAppNotification();

  const onSubmit = (values: CitationSchema) => {
    mutate(values, {
      onError: notification.error,
      onSuccess: notification.success,
    });
  };

  const values = useMemo(() => convert(citation), [citation]);
  const navigate = useNavigate();

  return (
    <CitationForm
      title={t('citations.form.title.edit')}
      schema={citationSchema}
      defaultValues={defaultCitationValues}
      values={values}
      onSubmit={onSubmit}
      isError={isError}
      isSuccess={isSuccess}
      isSubmitting={isPending}
      onCancel={() => navigate({ to: '..' })}
      showWosFields={false}
      showScopusFields={false}
    />
  );
}
